import { AutotractorPropsType, Domain } from "../types";
import { bearingToAzimuth, lineString } from "@turf/helpers";
import { CenterPivot } from "protos/farm/farm";
import { CenterPivotRange, Task } from "protos/rtc/jobs";
import { DeviceLocationHistoryRecord } from "protos/rtc/device_location_history";
import { farmApi } from "common/state/portalApi/farm";
import { FarmMapData } from "common/utils/farm";
import { FeatureCollection, Polygon } from "geojson";
import { jobExplorer } from "common/state/jobExplorer";
import { Layer, Marker, Source } from "react-map-gl";
import { makeTaskRenderer, TaskWith } from "../TaskLayers";
import { SvgIcon } from "@mui/material";
import { TaskFeatureProperties } from "common/utils/rtcJobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import { useFarmMapData } from "common/hooks/useFarmMapData";
import { useSelector } from "react-redux";
import along from "@turf/along";
import bearing from "@turf/bearing";
import CenterPivotTurnArrowIcon from "common/images/icons/center-pivot-turn-arrow.svg?react";
import React from "react";
import sector from "@turf/sector";

enum LayerIds {
  FILL = "mcp-fill",
  OUTLINE = "mcp-outline",
  LABEL = "mcp-label",
  TURN_ARROWS = "mcp-turn-arrows",
}

function turnArrowMarkerPositionsForCenterPivots(
  relatedPivots: CenterPivot[],
  livePivotData: {
    [deviceId: string]: {
      liveDeviceData: DeviceLocationHistoryRecord | undefined;
    };
  }
): { lat: number; lng: number; rotation: number }[] {
  return relatedPivots.flatMap((pivot) => {
    const liveLoc = livePivotData[pivot.endpointDeviceId]?.liveDeviceData;

    if (!pivot.center || !liveLoc?.point) {
      // don't have location data for this pivot; don't draw an arrow
      return [];
    }

    const ARROW_POSITION_ALONG_PIVOT_ARM = 0.75; // fraction of pivot length

    const pivotBearing = bearing(
      [pivot.center.lng, pivot.center.lat],
      [liveLoc.point.lng, liveLoc.point.lat]
    );

    const arrowPosition = along(
      lineString([
        [pivot.center.lng, pivot.center.lat],
        [liveLoc.point.lng, liveLoc.point.lat],
      ]),
      pivot.lengthMeters * ARROW_POSITION_ALONG_PIVOT_ARM,
      { units: "meters" }
    );

    return {
      lng: arrowPosition.geometry.coordinates.at(0) ?? 0,
      lat: arrowPosition.geometry.coordinates.at(1) ?? 0,
      rotation: pivotBearing,
    };
  });
}

function targetSectorsForCenterPivotTasks<TDetailsField extends keyof Task>(
  tasks: TaskWith<TDetailsField>[],
  getPivotRangeFromTask: (
    task: TaskWith<TDetailsField>
  ) => CenterPivotRange | undefined,
  farmMapData: FarmMapData | undefined
): FeatureCollection<Polygon, TaskFeatureProperties> {
  return {
    type: "FeatureCollection",
    features: tasks.flatMap((t) => {
      const pivotRange = getPivotRangeFromTask(t);
      if (!pivotRange?.center) {
        // can't really draw much without a known center point
        return [];
      }

      const { minHeading, maxHeading } = pivotRange;
      let headingDiff = maxHeading - minHeading;

      // normalize the heading difference by adding or removing turns as
      // necessary
      while (headingDiff > 360) {
        headingDiff -= 360;
      }

      while (headingDiff < 0) {
        headingDiff += 360;
      }

      // we know that the sector described by the task is smaller than a
      // half-turn.  compare the heading difference to a half-turn; if
      // max - min is the big half of the circle, max is counterclockwise of min
      let ccwHeading: number;
      let cwHeading: number;

      if (headingDiff > 180) {
        ccwHeading = maxHeading;
        cwHeading = minHeading;
      } else {
        ccwHeading = minHeading;
        cwHeading = maxHeading;
      }

      const movingPivot = farmMapData?.centerPivotsByCenterPointId.get(
        pivotRange.pivotId
      );
      // fall back to pretending the center pivot is 250m long for display's
      // sake if there's no center pivot on this field, or its length is zero;
      // unicorn suggests converting this to a ?? statement but that doesn't
      // work the same way
      // eslint-disable-next-line unicorn/prefer-logical-operator-over-ternary
      const sectorRadius = movingPivot?.lengthMeters
        ? movingPivot.lengthMeters
        : 250;

      const baseGeom = sector(
        [pivotRange.center.lng, pivotRange.center.lat],
        sectorRadius,
        ccwHeading,
        cwHeading,
        { units: "meters" }
      );

      return [
        {
          ...baseGeom,
          properties: {
            __portalFeatureProps: true,
            domain: Domain.AUTOTRACTOR,
            type: AutotractorPropsType.TASK,
            task: t,
            headingLabel: `${bearingToAzimuth(pivotRange.minHeading).toFixed(
              1
            )}°,${bearingToAzimuth(pivotRange.maxHeading).toFixed(1)}°`,
            maxHeading: pivotRange.maxHeading,
          },
        },
      ];
    }),
  };
}

function makeCenterPivotTaskRenderer<TDetailsField extends keyof Task>(
  detailsField: TDetailsField,
  getPivotRangeFromTask: (
    task: TaskWith<TDetailsField>
  ) => CenterPivotRange | undefined
): ReturnType<typeof makeTaskRenderer<TDetailsField>> {
  return makeTaskRenderer({
    detailsField,
    Render: ({ configuredJob, tasks, extraLayerProps, beforeId }) => {
      const { data: farm } = farmApi.endpoints.getFarm.useQuery(
        configuredJob.job.farmId
      );
      const farmMapData = useFarmMapData(farm);
      // identify the located center pivots associated with this set of tasks,
      // and fetch their location information
      const relatedPivots = tasks.flatMap((t) => {
        const centerPointId = getPivotRangeFromTask(t)?.pivotId;
        if (centerPointId) {
          return (
            farmMapData?.centerPivotsByCenterPointId.get(centerPointId) ?? []
          );
        } else {
          return [];
        }
      });
      const relatedPivotIds = relatedPivots.map(
        (pivot) => pivot.endpointDeviceId
      );
      const livePivotData = useSelector(
        (state: {
          jobExplorer: ReturnType<(typeof jobExplorer)["selectSlice"]>;
        }) =>
          jobExplorer.selectors.selectEndpointDeviceDataByDeviceIds(
            state,
            relatedPivotIds
          )
      );

      return (
        <>
          <Source
            data={targetSectorsForCenterPivotTasks(
              tasks,
              getPivotRangeFromTask,
              farmMapData
            )}
            type="geojson"
          >
            <Layer
              id={LayerIds.LABEL}
              beforeId={beforeId}
              type="symbol"
              layout={{
                "text-field": ["get", "headingLabel"],
              }}
              paint={{
                "text-color": "#FFFFFF",
                "text-halo-color": theme.colors.black,
                "text-halo-width": 2,
              }}
              {...extraLayerProps}
            />
            <Layer
              id={LayerIds.FILL}
              beforeId={beforeId}
              type="fill"
              paint={{
                "fill-color": "white",
                "fill-opacity": 0.2,
              }}
              {...extraLayerProps}
            />
            <Layer
              id={LayerIds.OUTLINE}
              beforeId={beforeId}
              type="line"
              {...extraLayerProps}
              paint={{
                "line-color": theme.colors.yellow[500],
                "line-width": 4,
              }}
            />
          </Source>
          {turnArrowMarkerPositionsForCenterPivots(
            relatedPivots,
            livePivotData
          ).map((pos, index) => (
            <Marker
              key={index}
              longitude={pos.lng}
              latitude={pos.lat}
              rotation={pos.rotation}
              rotationAlignment="map"
            >
              <SvgIcon
                sx={{ height: "2rem", width: "2rem" }}
                component={CenterPivotTurnArrowIcon}
                inheritViewBox={true}
                htmlColor={theme.colors.yellow[500]}
              />
            </Marker>
          ))}
        </>
      );
    },
    interactiveLayerIds: [LayerIds.FILL],
    imagesToLoad: [],
  });
}

export const renderMoveCenterPivotTask = makeCenterPivotTaskRenderer(
  "moveCenterPivot",
  (task) => task.moveCenterPivot.pivotRange
);

export const renderVerifyCenterPivotTask = makeCenterPivotTaskRenderer(
  "verifyCenterPivot",
  (task) => task.verifyCenterPivot.pivotRange
);
